import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, Box, IconButton, Button, Radio, RadioGroup, FormControlLabel, FormControl, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import { Close } from '@mui/icons-material';
import { headingBlue, red, green, mediumGrey, pureWhite, orange } from '../../../assets/colors';
import HttpService from '../../../backend/requestApis';
import { submitBatchAssessment, startAssessment, getAssessmentDetails } from '../../../backend/backendApis';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import Loader from '../../Loader';

const getDifficultyBadge = (difficulty) => {
    const diff = (difficulty || '').toLowerCase();
    let bg = '#eee';
    let fg = '#666';
    if (diff === 'easy') {
        bg = '#e8f5e9';
        fg = '#2e7d32';
    } else if (diff === 'medium') {
        bg = '#fff3e0';
        fg = '#ef6c00';
    } else if (diff === 'hard') {
        bg = '#ffebee';
        fg = '#c62828';
    }

    return (
        <span style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: bg,
            color: fg,
            marginLeft: '8px',
            verticalAlign: 'middle'
        }}>
            {diff}
        </span>
    );
};

const TakeAssessmentDialog = ({ dialogStatus, updateFunc, batchId, batchName }) => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [isStarted, setIsStarted] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes in seconds
    const [attempts, setAttempts] = useState([]);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [detailsLoading, setDetailsLoading] = useState(false);
    const dispatch = useDispatch();

    const activeAttempt = Array.isArray(attempts) ? attempts.find(att => att.status === 'started') : null;

    useEffect(() => {
        let interval;
        if (dialogStatus) {
            setCurrentTime(new Date());
            interval = setInterval(() => {
                setCurrentTime(new Date());
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [dialogStatus]);

    useEffect(() => {
        if (dialogStatus && batchId) {
            setIsStarted(false);
            setIsStarting(false);
            setQuestions([]);
            setAnswers({});
            setResult(null);
            setTimeLeft(1800);
            setAttempts([]);
            setDetailsLoading(true);

            HttpService({
                url: getAssessmentDetails,
                method: 'GET',
                params: { batchId },
                headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null }
            }).then(resp => {
                let data = resp?.data?.data || resp?.data || [];
                if (data && !Array.isArray(data)) {
                    data = [data];
                }
                setAttempts(data);
                setDetailsLoading(false);
            }).catch(err => {
                console.error("Error fetching assessment details:", err);
                setDetailsLoading(false);
            });
        }
    }, [dialogStatus, batchId]);

    const getAttemptTimes = (attempt) => {
        if (!attempt || !attempt.startTime) return { elapsed: 0, remaining: 0 };
        const startTimeMs = new Date(attempt.startTime).getTime();
        const nowMs = currentTime.getTime();
        const elapsedSeconds = Math.max(0, Math.floor((nowMs - startTimeMs) / 1000));
        const durationSeconds = (attempt.durationLimitMinutes || 30) * 60;
        const remainingSeconds = Math.max(0, durationSeconds - elapsedSeconds);
        return { elapsed: elapsedSeconds, remaining: remainingSeconds };
    };

    const handleResumeAssessment = (specificAttempt = null) => {
        const targetAttempt = specificAttempt || activeAttempt;
        if (!targetAttempt) return;
        const { remaining } = getAttemptTimes(targetAttempt);

        setQuestions(targetAttempt.questions || []);
        setTimeLeft(remaining);
        setIsStarted(true);
        dispatch(showSnackBar({ message: 'Resumed assessment successfully!', severity: 'success' }));
    };

    const handleStartAssessment = () => {
        setIsStarting(true);
        HttpService({
            url: startAssessment,
            method: 'POST',
            payload: { batchId },
            headers: { headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null } }
        }).then(resp => {
            if (resp?.data && resp.data.status === 0) {
                setIsStarting(false);
                dispatch(showSnackBar({ message: resp.data.description || resp.data.message || 'Error starting assessment.', severity: 'error' }));
            } else {
                const assessmentData = resp?.data?.data || {};
                const qList = assessmentData.questions || [];
                const durationLimit = assessmentData.durationLimitMinutes || 30;

                setQuestions(qList);
                setTimeLeft(durationLimit * 60);
                setIsStarted(true);
                setIsStarting(false);
                dispatch(showSnackBar({ message: `Assessment started successfully! You have ${durationLimit} minutes.`, severity: 'success' }));
            }
        }).catch(err => {
            console.error("Error starting assessment:", err);
            let errMsg = 'Error starting assessment.';
            if (err?.response?.data) {
                const data = err.response.data;
                if (data.description) {
                    if (typeof data.description === 'object') {
                        errMsg = data.description.msg || data.description.message || JSON.stringify(data.description);
                    } else {
                        errMsg = data.description;
                    }
                } else if (data.message) {
                    errMsg = data.message;
                }
            }
            dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
            setIsStarting(false);
        });
    };

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleAutoSubmit = () => {
        setIsSubmitting(true);
        dispatch(showSnackBar({ message: 'Time is up! Submitting your assessment automatically.', severity: 'warning' }));
        const payload = {
            batchId,
            responses: Object.keys(answers).map(qId => ({ questionId: qId, answer: answers[qId] }))
        };

        HttpService({
            url: submitBatchAssessment,
            method: 'POST',
            payload,
            headers: { headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null } }
        }).then(resp => {
            if (resp?.data && resp.data.status === 0) {
                const errMsg = resp.data.description || resp.data.message || 'Error submitting assessment automatically.';
                if (errMsg.includes("Assessment time limit exceeded") || errMsg.includes("time limit exceeded") || errMsg.includes("scored as zero")) {
                    setResult({
                        passed: false,
                        score: 0,
                        totalScore: questions.length,
                        percentage: 0,
                        message: errMsg
                    });
                }
                dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
                setIsSubmitting(false);
            } else {
                const data = resp?.data?.data || resp?.data || {};
                setResult(data);
                setIsSubmitting(false);
            }
        }).catch(err => {
            console.error("Error auto-submitting assessment:", err);
            const data = err?.response?.data;
            let errMsg = 'Error submitting assessment automatically.';
            if (data) {
                if (typeof data.description === 'object') {
                    errMsg = data.description.msg || data.description.message || JSON.stringify(data.description);
                } else if (data.description) {
                    errMsg = data.description;
                } else if (data.message) {
                    errMsg = data.message;
                }
            }

            if (errMsg.includes("Assessment time limit exceeded") || errMsg.includes("time limit exceeded") || errMsg.includes("scored as zero")) {
                setResult({
                    passed: false,
                    score: 0,
                    totalScore: questions.length,
                    percentage: 0,
                    message: errMsg
                });
            }
            dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
            setIsSubmitting(false);
        });
    };

    useEffect(() => {
        let timer;
        if (isStarted && !result && !isSubmitting && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
        } else if (timeLeft === 0 && isStarted && !result && !isSubmitting) {
            handleAutoSubmit();
        }
        return () => clearInterval(timer);
    }, [isStarted, result, isSubmitting, timeLeft]);

    const handleOptionChange = (qId, value) => {
        setAnswers(prev => ({ ...prev, [qId]: value }));
    };

    const handleSubmit = () => {
        if (Object.keys(answers).length < questions.length) {
            dispatch(showSnackBar({ message: 'Please answer all questions before submitting.', severity: 'warning' }));
            return;
        }
        setIsSubmitting(true);
        const payload = {
            batchId,
            responses: Object.keys(answers).map(qId => ({ questionId: qId, answer: answers[qId] }))
        };

        HttpService({
            url: submitBatchAssessment,
            method: 'POST',
            payload,
            headers: { headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null } }
        }).then(resp => {
            if (resp?.data && resp.data.status === 0) {
                const errMsg = resp.data.description || resp.data.message || 'Error submitting assessment.';
                if (errMsg.includes("Assessment time limit exceeded") || errMsg.includes("time limit exceeded") || errMsg.includes("scored as zero")) {
                    setResult({
                        passed: false,
                        score: 0,
                        totalScore: questions.length,
                        percentage: 0,
                        message: errMsg
                    });
                }
                dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
                setIsSubmitting(false);
            } else {
                const data = resp?.data?.data || resp?.data || {};
                setResult(data);
                dispatch(showSnackBar({ message: 'Assessment submitted successfully!', severity: 'success' }));
                setIsSubmitting(false);
            }
        }).catch(err => {
            console.error("Error submitting assessment:", err);
            const data = err?.response?.data;
            let errMsg = 'Error submitting assessment.';
            if (data) {
                if (typeof data.description === 'object') {
                    errMsg = data.description.msg || data.description.message || JSON.stringify(data.description);
                } else if (data.description) {
                    errMsg = data.description;
                } else if (data.message) {
                    errMsg = data.message;
                }
            }

            if (errMsg.includes("Assessment time limit exceeded") || errMsg.includes("time limit exceeded") || errMsg.includes("scored as zero")) {
                setResult({
                    passed: false,
                    score: 0,
                    totalScore: questions.length,
                    percentage: 0,
                    message: errMsg
                });
            }
            dispatch(showSnackBar({ message: errMsg, severity: 'error' }));
            setIsSubmitting(false);
        });
    };

    const easyQuestions = questions.filter(q => (q.difficulty || '').toLowerCase() === 'easy');
    const mediumQuestions = questions.filter(q => (q.difficulty || '').toLowerCase() === 'medium');
    const hardQuestions = questions.filter(q => (q.difficulty || '').toLowerCase() === 'hard');
    const otherQuestions = questions.filter(q => {
        const diff = (q.difficulty || '').toLowerCase();
        return diff !== 'easy' && diff !== 'medium' && diff !== 'hard';
    });

    const renderDifficultySection = (title, filteredQuestions, sectionTextColor, sectionBgColor, sectionBorderColor) => {
        if (filteredQuestions.length === 0) return null;

        return (
            <Box style={{
                marginBottom: '2rem',
                border: `1px solid ${sectionBorderColor}`,
                borderRadius: '8px',
                backgroundColor: 'white',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
                <Box style={{
                    padding: '0.75rem 1rem',
                    backgroundColor: sectionBgColor,
                    borderBottom: `1px solid ${sectionBorderColor}`,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <Typography style={{ fontWeight: 700, color: sectionTextColor, textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.5px' }}>
                        {title} ({filteredQuestions.length})
                    </Typography>
                </Box>

                <Box style={{ padding: '1.25rem' }}>
                    {filteredQuestions.map((q, qIdx) => {
                        const overallIdx = questions.findIndex(item => item._id === q._id);
                        const displayIdx = overallIdx !== -1 ? overallIdx + 1 : 1;

                        return (
                            <Box key={q._id || q.id} style={{
                                marginBottom: qIdx === filteredQuestions.length - 1 ? 0 : '1.5rem',
                                paddingBottom: qIdx === filteredQuestions.length - 1 ? 0 : '1.5rem',
                                borderBottom: qIdx === filteredQuestions.length - 1 ? 'none' : `1px dashed ${mediumGrey}`
                            }}>
                                <Typography style={{ fontWeight: 600, color: headingBlue, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                    <span>{displayIdx}. {q.questionText || q.question}</span>
                                    {getDifficultyBadge(q.difficulty)}
                                </Typography>
                                <FormControl component="fieldset" style={{ width: '100%' }}>
                                    <RadioGroup value={answers[q._id || q.id] || ''} onChange={(e) => handleOptionChange(q._id || q.id, e.target.value)}>
                                        {(q.options || []).map((opt, oIdx) => {
                                            const optText = typeof opt === 'object' ? (opt.text || opt.option) : opt;
                                            return (
                                                <FormControlLabel
                                                    key={oIdx}
                                                    value={optText}
                                                    control={<Radio size="small" style={{ color: headingBlue }} />}
                                                    label={<Typography style={{ fontSize: '0.9rem', color: '#333' }}>{optText}</Typography>}
                                                />
                                            );
                                        })}
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    const renderAttemptsTable = () => {
        if (!attempts || attempts.length === 0) return null;

        return (
            <Box style={{ marginTop: '2rem' }}>
                <Typography style={{ fontWeight: 700, color: headingBlue, fontSize: '1.15rem', marginBottom: '1rem', textAlign: 'left' }}>
                    Attempt History
                </Typography>
                <TableContainer component={Paper} elevation={0} style={{ border: '1px solid #e0e0e0', borderRadius: '8px' }} sx={{ "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none" }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }}>Attempt #</TableCell>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }}>Date & Time</TableCell>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }}>Score</TableCell>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }}>Percentage</TableCell>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }}>Status</TableCell>
                                <TableCell style={{ fontWeight: 600, color: headingBlue }} align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {attempts.map((att, index) => {
                                const attemptNum = attempts.length - index;
                                const isCompleted = att.status === 'completed';
                                const isStarted = att.status === 'started';

                                return (
                                    <TableRow key={att._id || index} hover>
                                        <TableCell style={{ fontWeight: 500 }}>{attemptNum}</TableCell>
                                        <TableCell>{new Date(att.startTime || att.createdAt).toLocaleString()}</TableCell>
                                        <TableCell>
                                            {isCompleted ? `${att.score ?? 0} / ${att.maxScore ?? 0}` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {isCompleted ? `${att.percentage ?? 0}%` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {isCompleted ? (
                                                <Chip
                                                    label={att.passed ? "Passed" : "Failed"}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: att.passed ? '#e8f5e9' : '#ffebee',
                                                        color: att.passed ? '#2e7d32' : '#c62828',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    label="In Progress"
                                                    size="small"
                                                    style={{
                                                        backgroundColor: '#fff3e0',
                                                        color: '#ef6c00',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            {isStarted && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={() => handleResumeAssessment(att)}
                                                    style={{
                                                        backgroundColor: headingBlue,
                                                        color: 'white',
                                                        textTransform: 'capitalize',
                                                        padding: '2px 10px',
                                                        fontSize: '0.75rem'
                                                    }}
                                                >
                                                    Resume
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            open={dialogStatus}
            onClose={(event, reason) => {
                if (isStarted && !result) return;
                if (!isSubmitting) updateFunc(false);
            }}
        >
            <DialogTitle style={{ backgroundColor: headingBlue, color: pureWhite, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 1rem' }}>
                <Typography style={{ fontWeight: 600, fontSize: '1rem' }}>Assessment: {batchName}</Typography>
                {(!isStarted || result) && (
                    <IconButton onClick={() => updateFunc(false)} style={{ color: pureWhite, padding: 0 }} disabled={isSubmitting}>
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: '1.5rem !important',
                    backgroundColor: '#fdfdfd',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: isStarted && !result ? 'hidden' : 'auto',
                    "&::-webkit-scrollbar": { display: 'none' },
                    scrollbarWidth: "none"
                }}
            >
                {isLoading || isSubmitting || isStarting ? (
                    <Box style={{ height: '30vh', position: 'relative' }}>
                        <Loader />
                    </Box>
                ) : !isStarted ? (
                    detailsLoading ? (
                        <Box style={{ height: '30vh', position: 'relative' }}>
                            <Loader />
                        </Box>
                    ) : (
                        <Box style={{ textAlign: 'center', padding: '1rem 1rem' }}>
                            {activeAttempt ? (
                                <Box>
                                    <Typography style={{ fontSize: '1.25rem', fontWeight: 600, color: orange, marginBottom: '1rem' }}>
                                        Assessment In Progress
                                    </Typography>
                                    <Typography style={{ fontSize: '1rem', color: '#333', marginBottom: '1.5rem' }}>
                                        You have an active attempt in progress for this training batch.
                                    </Typography>

                                    <Box style={{
                                        maxWidth: '350px',
                                        margin: '0 auto 2rem auto',
                                        padding: '1rem',
                                        border: `1px solid ${orange}`,
                                        borderRadius: '8px',
                                        backgroundColor: '#fff3e0',
                                        textAlign: 'left'
                                    }}>
                                        <Typography style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>
                                            <strong>Started:</strong> {new Date(activeAttempt.startTime).toLocaleTimeString()}
                                        </Typography>
                                        <Typography style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.5rem' }}>
                                            <strong>Time Elapsed:</strong> {formatTime(getAttemptTimes(activeAttempt).elapsed)}
                                        </Typography>
                                        <Typography style={{ fontSize: '0.9rem', color: orange, fontWeight: 600 }}>
                                            <strong>Time Remaining:</strong> {formatTime(getAttemptTimes(activeAttempt).remaining)}
                                        </Typography>
                                    </Box>

                                    <Box style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleStartAssessment}
                                            disabled={true}
                                            style={{ backgroundColor: '#ccc', color: 'white', textTransform: 'capitalize', padding: '0.4rem 2rem' }}
                                        >
                                            Start
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={() => handleResumeAssessment(activeAttempt)}
                                            style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize', padding: '0.4rem 2rem' }}
                                        >
                                            Resume
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography style={{ fontSize: '1.25rem', fontWeight: 600, color: headingBlue, marginBottom: '1rem' }}>
                                        Start Assessment Confirmation
                                    </Typography>
                                    <Typography style={{ fontSize: '1rem', color: '#333', marginBottom: '2rem' }}>
                                        Assessment will be started, you have 30 mins to complete the assessment.
                                    </Typography>
                                    <Box style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                        <Button
                                            variant="contained"
                                            onClick={handleStartAssessment}
                                            style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize', padding: '0.4rem 2rem' }}
                                        >
                                            Start
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {renderAttemptsTable()}
                        </Box>
                    )
                ) : result ? (
                    <Box style={{ textAlign: 'center', padding: '2rem' }}>
                        <Typography style={{ fontSize: '1.5rem', fontWeight: 600, color: result.passed ? green : red, marginBottom: '1rem' }}>
                            {result.passed ? 'Congratulations! You Passed.' : 'You did not qualify the assessment.'}
                        </Typography>
                        {result.message && (
                            <Typography style={{ fontSize: '1.05rem', color: red, marginBottom: '1.5rem', fontWeight: 500 }}>
                                {result.message}
                            </Typography>
                        )}
                        <Typography style={{ fontSize: '1.1rem', color: headingBlue }}>
                            Score: {result.score} / {result.totalScore || questions.length}
                        </Typography>
                        <Typography style={{ fontSize: '1rem', color: '#666', marginTop: '0.5rem' }}>
                            Percentage: {result.percentage}%
                        </Typography>
                        <Button variant="contained" onClick={() => updateFunc(false, true)} style={{ backgroundColor: headingBlue, color: 'white', marginTop: '2rem', textTransform: 'capitalize' }}>
                            Close
                        </Button>
                    </Box>
                ) : questions.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '65vh' }}>
                        {/* Static Timer Box */}
                        <Box style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1.5rem',
                            padding: '0.6rem 1rem',
                            backgroundColor: '#fff3e0',
                            borderRadius: '6px',
                            border: `1px solid ${orange}`
                        }}>
                            <Typography style={{ fontWeight: 600, color: orange, fontSize: '0.95rem' }}>
                                Time Remaining: {formatTime(timeLeft)}
                            </Typography>
                            <Typography style={{ fontSize: '0.8rem', color: '#666' }}>
                                Please do not close or refresh this window.
                            </Typography>
                        </Box>

                        {/* Scrollable Questions Container */}
                        <Box sx={{
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '4px',
                            "&::-webkit-scrollbar": { display: 'none' },
                            scrollbarWidth: "none",
                            marginBottom: '1rem'
                        }}>
                            {renderDifficultySection('Easy Level Questions', easyQuestions, '#2e7d32', '#e8f5e9', '#c8e6c9')}
                            {renderDifficultySection('Medium Level Questions', mediumQuestions, '#ef6c00', '#fff3e0', '#ffe0b2')}
                            {renderDifficultySection('Hard Level Questions', hardQuestions, '#c62828', '#ffebee', '#ffcdd2')}
                            {renderDifficultySection('General Questions', otherQuestions, '#1565c0', '#e3f2fd', '#bbdefb')}
                        </Box>

                        {/* Static Submit Button Bar */}
                        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
                            <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting} style={{ backgroundColor: headingBlue, color: 'white', textTransform: 'capitalize', padding: '0.5rem 2rem' }}>
                                Submit Assessment
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Typography style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No questions available for this assessment.</Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default TakeAssessmentDialog;